import React from 'react';
import { AcademicSystem } from '../types';

type SetupFormProps = {
  options: Array<{ id: AcademicSystem; title: string; detail: string }>;
  selection: AcademicSystem | null;
  startYear: string;
  classesPerTerm: string;
  classesToGraduate: string;
  onSelectionChange: (value: AcademicSystem) => void;
  onStartYearChange: (value: string) => void;
  onClassesPerTermChange: (value: string) => void;
  onClassesToGraduateChange: (value: string) => void;
  onAddRequirement: () => void;
  onUpdateRequirement: (id: string, field: 'name' | 'count', value: string) => void;
  onRemoveRequirement: (id: string) => void;
  onContinue: () => void;
};

const SetupForm: React.FC<SetupFormProps> = ({
  options,
  selection,
  startYear,
  classesPerTerm,
  classesToGraduate,
  onSelectionChange,
  onStartYearChange,
  onClassesPerTermChange,
  onClassesToGraduateChange,
  onContinue,
}) => (
  <form className="mt-10 space-y-8">
    <fieldset className="space-y-4 rounded-xl border border-slate-200 bg-white/80 p-6 shadow-[0_12px_50px_-45px_rgba(15,23,42,0.55)]">
      <legend className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Academic calendar</legend>
      <p className="text-base text-slate-700">Is your school on a quarter or semester system?</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {options.map((option) => (
          <label
            key={option.id}
            className={`group flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition duration-200 ${
              selection === option.id
                ? 'border-slate-900 bg-white shadow-[0_10px_40px_-32px_rgba(0,0,0,0.55)]'
                : 'border-slate-200 bg-white/90 hover:border-slate-400'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="academic-system"
                value={option.id}
                className="mt-1 h-4 w-4 border-slate-300 text-slate-900 focus:ring-slate-900"
                checked={selection === option.id}
                onChange={() => onSelectionChange(option.id)}
              />
              <div>
                <span className="text-lg font-semibold text-slate-900">{option.title}</span>
                <p className="text-sm text-slate-600">{option.detail}</p>
              </div>
            </div>
          </label>
        ))}
      </div>
      <label className="mt-4 flex flex-col gap-2 text-sm font-medium text-slate-800">
        Starting year (optional)
        <input
          type="number"
          inputMode="numeric"
          min={2025}
          value={startYear}
          onChange={(event) => onStartYearChange(event.target.value)}
          placeholder="e.g., 2025"
          className="rounded-lg border border-slate-200 px-3 py-2 text-base font-normal text-slate-900 shadow-inner shadow-slate-100 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
        />
      </label>
    </fieldset>

    <fieldset className="space-y-4 rounded-xl border border-slate-200 bg-white/80 p-6 shadow-[0_12px_50px_-45px_rgba(15,23,42,0.55)]">
      <legend className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Course pacing</legend>
      <div className="grid gap-6 md:grid-cols-2">
        <label className="flex flex-col gap-2 text-sm font-medium text-slate-800">
          How many classes do you plan to take each term?
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={classesPerTerm}
            onChange={(event) => onClassesPerTermChange(event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-base font-normal text-slate-900 shadow-inner shadow-slate-100 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </label>
        <label className="flex flex-col gap-2 text-sm font-medium text-slate-800">
          How many classes must you take to graduate?
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={classesToGraduate}
            onChange={(event) => onClassesToGraduateChange(event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-base font-normal text-slate-900 shadow-inner shadow-slate-100 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </label>
      </div>
    </fieldset>

    <div className="flex items-center gap-4">
      <div className="h-px flex-1 bg-gradient-to-r from-slate-200 via-slate-300 to-transparent" />
      <button
        type="button"
        disabled={!selection}
        onClick={onContinue}
        className="rounded-full border border-slate-900 px-6 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-900 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-28px_rgba(15,23,42,0.55)] disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:shadow-none"
      >
        Continue to yearly plan
      </button>
    </div>
  </form>
);

export default SetupForm;
